import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as analytics from '@adsk/acs-web-analytics';
import { Box, Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import SearchField from '@adsk/alloy-react-search-field';
import Tabs, { Tab } from '@adsk/alloy-react-tabs';
import { TABS_NAMES, PROJECT_ENTITLEMENTS_LIMIT, SEARCH_PLACEHOLDER_KEYS } from './consts';
import ProjectsError from './ProjectsError';
import ResultsLoader from './ResultsLoader';
import VirtualizedList from './VirtualizedList';
import { ANALYTICS_DEBOUNCE, ANALYTICS_TYPES } from '../helpers/analytics';
import { accountsListShape } from '../helpers/propTypes';
import { useDebounce } from '../hooks/useDebounce';
import { useProjectPickerTabs } from '../hooks/useProjectPickerTabs';
const ProjectPicker = _ref => {
  var _T$translate;
  let {
    accounts,
    currentAccountId,
    currentProjectId,
    currentProductId,
    onClose,
    onAccountClick,
    onProjectClick
  } = _ref;
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  const searchRef = useRef(null);
  const containerRef = useRef(null);
  const hasAccounts = accounts !== undefined;
  const [currentTab, setCurrentTab] = useState(hasAccounts ? TABS_NAMES.ACCOUNTS : TABS_NAMES.PROJECTS);
  const [search, setSearch] = useState('');
  const reportSearchOnChangeAnalyticFn = useCallback(() => debounceSearch => {
    if (debounceSearch && debounceSearch !== '') {
      analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_SEARCH_CHANGED, {
        product_id: currentProductId,
        searched_text: debounceSearch
      });
    }
  }, [currentProductId]);
  const debouncedSearch = useDebounce(search, ANALYTICS_DEBOUNCE, reportSearchOnChangeAnalyticFn);
  const onSearchChange = value => {
    setSearch(value);
  };
  const tabs = useProjectPickerTabs({
    search: debouncedSearch,
    accounts,
    hasAccounts,
    currentTab,
    onClose
  });
  useEffect(() => {
    var _searchRef$current;
    return (_searchRef$current = searchRef.current) === null || _searchRef$current === void 0 ? void 0 : _searchRef$current.focus();
  }, []);
  return /*#__PURE__*/React.createElement(Box, {
    width: "360px",
    ref: containerRef
  }, /*#__PURE__*/React.createElement(Box, {
    padding: "16px"
  }, /*#__PURE__*/React.createElement(SearchField, {
    ref: searchRef,
    placeholder: (_T$translate = T.translate(SEARCH_PLACEHOLDER_KEYS[currentTab])) === null || _T$translate === void 0 ? void 0 : _T$translate.toString(),
    value: search || '',
    onChange: onSearchChange
  })), /*#__PURE__*/React.createElement(Tabs, {
    labelWidth: "full",
    initialTab: currentTab,
    onChange: tab => setCurrentTab(tab)
  }, tabs.map(tab => {
    var _tab$data, _tab$data2, _tab$data3, _tab$data4, _tab$data5, _tab$data6;
    return tab && /*#__PURE__*/React.createElement(Tab, {
      key: tab.tab,
      tab: tab.tab,
      label: tab.label,
      labelProps: {
        'data-testid': "ProjectPicker_tab_".concat(tab.tab)
      }
    }, !tab.error && /*#__PURE__*/React.createElement(Box, {
      typography: "heading3",
      color: "charcoal700",
      padding: "8px 16px"
    }, /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "space-between",
      alignItems: "center"
    }, tab.loading ? /*#__PURE__*/React.createElement(ResultsLoader, null) : /*#__PURE__*/React.createElement("div", {
      "data-testid": "ProjectPicker_tab_totalResults"
    }, !!tab.totalResults && "".concat(tab.totalResults, " ").concat(tab.label)), /*#__PURE__*/React.createElement(LinkButton, {
      as: "a",
      size: LinkButton.SIZES.MEDIUM,
      href: tab.viewAllLink,
      onClick: () => {
        analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_VIEWALL_CLICK);
      }
    }, tab.viewAllLabel))), debouncedSearch && !((_tab$data = tab.data) !== null && _tab$data !== void 0 && _tab$data.length) && !tab.loading && !tab.error && /*#__PURE__*/React.createElement(Box, {
      typography: "bodySmall",
      color: "charcoal700",
      textAlign: "center",
      padding: "36px 0"
    }, T.translate('NO_RESULTS_FOUND')), !debouncedSearch && !((_tab$data2 = tab.data) !== null && _tab$data2 !== void 0 && _tab$data2.length) && !tab.loading && !tab.error && /*#__PURE__*/React.createElement(Box, {
      typography: "bodySmall",
      color: "charcoal700",
      textAlign: "center",
      padding: "36px 0"
    }, T.translate('EMPTY_RESULTS', {
      context: tab.label
    })), !((_tab$data3 = tab.data) !== null && _tab$data3 !== void 0 && _tab$data3.length) && tab.loading && tab.renderLoader(), !((_tab$data4 = tab.data) !== null && _tab$data4 !== void 0 && _tab$data4.length) && tab.error && /*#__PURE__*/React.createElement(ProjectsError, {
      retry: tab.retry,
      projectsCount: (_tab$data5 = tab.data) === null || _tab$data5 === void 0 ? void 0 : _tab$data5.length
    }), !!((_tab$data6 = tab.data) !== null && _tab$data6 !== void 0 && _tab$data6.length) && /*#__PURE__*/React.createElement(VirtualizedList, {
      size: tab.data.length,
      fetchNextPage: tab.fetchNextPage,
      isFetchingNextPage: tab.loading,
      renderLoader: tab.renderLoader,
      renderItem: index => {
        var _tab$data7;
        return tab.renderItem({
          item: (_tab$data7 = tab.data) === null || _tab$data7 === void 0 ? void 0 : _tab$data7[index],
          search: debouncedSearch,
          currentAccountId,
          currentProjectId,
          currentProductId,
          onAccountClick,
          onProjectClick,
          containerRef
        });
      },
      hasNextPage: tab.hasNextPage && tab.data.length >= PROJECT_ENTITLEMENTS_LIMIT
    }));
  })));
};
ProjectPicker.propTypes = {
  /** Available accounts to select from */
  accounts: accountsListShape,
  /** current selected account id */
  currentAccountId: PropTypes.string,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** current product ID, to check if project has entitlements for or not */
  currentProductId: PropTypes.string,
  /** menu close handler */
  onClose: PropTypes.func,
  /** account click handler */
  onAccountClick: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func
};
export default ProjectPicker;
//# sourceMappingURL=ProjectPicker.js.map